import React, { FC, Suspense, useState } from 'react';
import { Icon } from 'src/components/widgets/icon';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { Drawer } from 'src/components/widgets/drawer';
import { ZTooltip } from 'src/components/widgets/ztooltip';
import { T } from 'src/shared/helpers';
import type { addAreaBackgroundRulePanel_AreaBackgroundRulesListForAddQuery } from './__generated__/addAreaBackgroundRulePanel_AreaBackgroundRulesListForAddQuery.graphql';
import type { addAreaBackgroundRulePanel_AssignProductMutation } from './__generated__/addAreaBackgroundRulePanel_AssignProductMutation.graphql';
import { PanelLinesSkeleton } from '../common/skeletons';

import {
    StackedBody,
    StackedFooterButtons,
    FooterButton,
    PanelItem,
    ItemDot,
    ItemName,
    ItemActionSuccess,
    PanelItemSpaced,
    ItemNameAction,
    EmptyState
} from '../common/styled';

const AddAreaBackgroundRulePanelContent: FC<{
    productId: number;
    existingRuleIds: string[];
    onBack: () => void;
    onAssigned: () => void;
    onCreateNew: () => void;
    productRelayId: string;
}> = ({ productId, productRelayId, existingRuleIds, onBack, onAssigned, onCreateNew }) => {
    const [assigning, setAssigning] = useState<string | null>(null);

    const productNameData = useLazyLoadQuery<any>(
        graphql`
            query addAreaBackgroundRulePanel_ProductNameQuery($id: ID!) {
                node(id: $id) {
                    ... on Model {
                        name
                    }
                }
            }
        `,
        { id: productRelayId }
    );
    const productName = (productNameData?.node as any)?.name || '';

    const data = useLazyLoadQuery<addAreaBackgroundRulePanel_AreaBackgroundRulesListForAddQuery>(
        graphql`
            query addAreaBackgroundRulePanel_AreaBackgroundRulesListForAddQuery {
                areaBackgroundRules {
                    id
                    name
                    printTypeIDs
                }
            }
        `,
        {}
    );

    const allRules = React.useMemo(() => {
        const rules = data?.areaBackgroundRules || [];
        return rules.map((rule) => ({
            id: rule.id,
            name: rule.name || rule.id,
            isAssignedToPrintType: (rule.printTypeIDs?.length ?? 0) > 0
        }));
    }, [data]);

    const [commitAssign] = useMutation<addAreaBackgroundRulePanel_AssignProductMutation>(graphql`
        mutation addAreaBackgroundRulePanel_AssignProductMutation($ruleId: ID!, $productIds: [Long!]!) {
            assignProductsToAreaBackgroundRule(ruleId: $ruleId, productIds: $productIds) {
                result
            }
        }
    `);

    const handleAssign = (rule: { id: string; name: string }) => {
        setAssigning(rule.id);
        commitAssign({
            variables: {
                ruleId: rule.id,
                productIds: [productId]
            },
            onCompleted: () => {
                setAssigning(null);
                onAssigned();
            },
            onError: () => {
                setAssigning(null);
            }
        });
    };

    const availableRules = allRules.filter((rule) => !existingRuleIds.includes(rule.id));
    const printTypeAssignedTooltip = T._(
        "This rule is assigned to a printing method and can't be assigned to a product. Either assign that printing method to this product, or change the rule assignment from the tool page.",
        'Admin'
    );

    return (
        <Drawer
            isOpen={true}
            isStacked
            onClose={onBack}
            header={{ title: T._('Add Fill Background rule', 'Admin'), subtitle: productName }}
            width={580}
            footer={
                <StackedFooterButtons>
                    <FooterButton onClick={onBack}>{T._('Cancel', 'Admin')}</FooterButton>
                </StackedFooterButtons>
            }
        >
            <StackedBody>
                {availableRules.length === 0 && <EmptyState>{T._('All rules are already assigned', 'Admin')}</EmptyState>}
                {availableRules.map((rule) => (
                    <ZTooltip key={rule.id} title={rule.isAssignedToPrintType ? printTypeAssignedTooltip : undefined} position='top' addContainer>
                        <PanelItem
                            $clickable={!rule.isAssignedToPrintType && assigning === null}
                            $disabled={rule.isAssignedToPrintType}
                            onClick={rule.isAssignedToPrintType || assigning !== null ? undefined : () => handleAssign(rule)}
                        >
                            <ItemDot $color='#3b82f6' />
                            <ItemName>{rule.name}</ItemName>
                            <ItemActionSuccess>{assigning === rule.id ? T._('Assigning...', 'Admin') : T._('Assign', 'Admin')}</ItemActionSuccess>
                        </PanelItem>
                    </ZTooltip>
                ))}
                <PanelItemSpaced
                    $clickable
                    onClick={() => {
                        onBack();
                        onCreateNew();
                    }}
                >
                    <ItemDot $color='#d1d5db' />
                    <ItemNameAction>
                        <Icon>
                            <PlusIcon />
                        </Icon>
                        {T._('Create new', 'Admin')}
                    </ItemNameAction>
                </PanelItemSpaced>
            </StackedBody>
        </Drawer>
    );
};

export const AddAreaBackgroundRulePanel: FC<{
    productId: number;
    productRelayId: string;
    existingRuleIds: string[];
    onBack: () => void;
    onAssigned: () => void;
    onCreateNew: () => void;
}> = (props) => {
    return (
        <Suspense
            fallback={
                <Drawer
                    isOpen={true}
                    isStacked
                    onClose={props.onBack}
                    header={{ title: T._('Add Fill Background rule', 'Admin'), subtitle: '' }}
                    width={580}
                    footer={
                        <StackedFooterButtons>
                            <FooterButton onClick={props.onBack}>{T._('Cancel', 'Admin')}</FooterButton>
                        </StackedFooterButtons>
                    }
                >
                    <StackedBody>
                        <PanelLinesSkeleton />
                    </StackedBody>
                </Drawer>
            }
        >
            <AddAreaBackgroundRulePanelContent {...props} />
        </Suspense>
    );
};
